import { supabase } from "@/integrations/supabase/client";
import { TokenUsageParams } from "@/types/tokens";
import { updateFreeQuota, checkFreeQuota } from "./token/quotaService";
import { logTokenError } from "./token/errorService";

class TokenService {
  private static instance: TokenService;
  private cache: Map<string, any> = new Map();
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes

  private constructor() {}

  static getInstance(): TokenService {
    if (!TokenService.instance) {
      TokenService.instance = new TokenService();
    }
    return TokenService.instance;
  }

  async checkTokenAvailability(tokensRequired: number, serviceName: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      // Get user's profile to check subscription status
      const { data: profile } = await supabase
        .from('profiles')
        .select('subscription_status')
        .eq('id', user.id)
        .single();

      // For free users, check their free quotas instead of tokens
      if (profile?.subscription_status === 'free') {
        switch (serviceName) {
          case 'document-generation':
            return checkFreeQuota(user.id, 'document');
          case 'ai-chat':
            return checkFreeQuota(user.id, 'ai');
          case 'case-creation':
            return checkFreeQuota(user.id, 'case');
          default:
            return false;
        }
      }

      // For paid users, check token balance
      const cacheKey = `allocation_${user.id}`;
      let allocation = this.cache.get(cacheKey);

      if (!allocation) {
        const { data, error } = await supabase
          .from('token_allocations')
          .select('monthly_tokens, tokens_used')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) throw error;
        if (!data) return false;

        allocation = data;
        this.cache.set(cacheKey, allocation);
        setTimeout(() => this.cache.delete(cacheKey), this.cacheTimeout);
      }

      return (allocation.monthly_tokens - allocation.tokens_used) >= tokensRequired;
    } catch (error) {
      console.error('Error checking token availability:', error);
      await logTokenError('token_availability_check', error);
      return false;
    }
  }

  async recordTokenUsage({ page, action, tokensUsed, metadata = {} }: TokenUsageParams): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { data: profile } = await supabase
        .from('profiles')
        .select('subscription_status')
        .eq('id', user.id)
        .single();

      // For free users, update their quota instead of token usage
      if (profile?.subscription_status === 'free') {
        const quotaType = this.getQuotaType(page);
        if (quotaType) {
          await updateFreeQuota(user.id, quotaType);
          return;
        }
      }

      // For paid users, record normal token usage
      await this.recordPaidUserTokenUsage(user.id, page, action, tokensUsed, metadata);

    } catch (error) {
      console.error('Error recording token usage:', error);
      await logTokenError('token_usage_recording', error);
      throw error;
    }
  }

  private async recordPaidUserTokenUsage(
    userId: string,
    page: string,
    action: string,
    tokensUsed: number,
    metadata: Record<string, any>
  ): Promise<void> {
    const { error: logError } = await supabase
      .from('token_usage_logs')
      .insert({
        user_id: userId,
        service_name: page,
        action_type: action,
        tokens_used: tokensUsed,
        metadata,
        created_at: new Date().toISOString()
      });

    if (logError) throw logError;

    await supabase.rpc('increment_token_usage', {
      p_user_id: userId,
      p_tokens_used: tokensUsed
    });

    this.cache.delete(`allocation_${userId}`);
    this.cache.delete(`usage_${userId}`);
  }

  private getQuotaType(page: string): 'document' | 'ai' | 'case' | null {
    switch (page) {
      case 'document-generation':
        return 'document';
      case 'ai-chat':
        return 'ai';
      case 'case-creation':
        return 'case';
      default:
        return null;
    }
  }

  async getTokenStats(userId: string) {
    const cacheKey = `usage_${userId}`;
    let stats = this.cache.get(cacheKey);

    if (!stats) {
      const [allocation, recentUsage] = await Promise.all([
        this.getAllocation(userId),
        this.getRecentUsage(userId)
      ]);

      stats = {
        monthlyTokens: allocation.monthly_tokens,
        tokensUsed: allocation.tokens_used,
        recentUsage,
        usagePercentage: (allocation.tokens_used / allocation.monthly_tokens) * 100
      };

      this.cache.set(cacheKey, stats);
      setTimeout(() => this.cache.delete(cacheKey), this.cacheTimeout);
    }

    return stats;
  }

  private async getAllocation(userId: string) {
    const { data, error } = await supabase
      .from('token_allocations')
      .select('monthly_tokens, tokens_used')
      .eq('user_id', userId)
      .single();

    if (error) throw error;
    return data;
  }

  private async getRecentUsage(userId: string) {
    const twentyFourHoursAgo = new Date();
    twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);

    const { data, error } = await supabase
      .from('token_usage_logs')
      .select('tokens_used')
      .eq('user_id', userId)
      .gte('created_at', twentyFourHoursAgo.toISOString());

    if (error) throw error;
    return data?.reduce((sum, log) => sum + (log.tokens_used || 0), 0) ?? 0;
  }
}

export const tokenService = TokenService.getInstance();